import React, { useMemo, useState } from 'react';
import { Sparkles, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEVERITY_COLORS, KIND_LABEL } from './_shared';

export default function CleanupTab({ queue, counts, onScan, onApprove, onDismiss, scanning }) {
  const [filter, setFilter] = useState('all');
  const filtered = useMemo(
    () => (queue || []).filter(r => filter === 'all' || r.kind === filter),
    [queue, filter]
  );

  const filterOpts = [
    { key: 'all', label: 'All' },
    { key: 'stale', label: `Stale (${counts.stale || 0})` },
    { key: 'duplicate', label: `Duplicates (${counts.duplicate || 0})` },
    { key: 'orphan', label: `Orphans (${counts.orphan || 0})` },
    { key: 'noisy_alert', label: `Noisy (${counts.noisy_alert || 0})` },
    { key: 'unused_ai_model', label: `Unused AI (${counts.unused_ai_model || 0})` },
  ];

  return (
    <div className="space-y-4" data-testid="governance-cleanup">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {filterOpts.map(o => (
            <button
              key={o.key}
              data-testid={`cleanup-filter-${o.key}`}
              onClick={() => setFilter(o.key)}
              className={`text-xs px-3 py-1 rounded-full border transition ${
                filter === o.key
                  ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700'
              }`}
            >{o.label}</button>
          ))}
        </div>
        <Button size="sm" onClick={onScan} disabled={scanning} data-testid="run-cleanup-scan-btn">
          {scanning ? <><RefreshCw size={14} className="mr-1 animate-spin" /> Scanning…</> : <><RefreshCw size={14} className="mr-1" /> Run scan now</>}
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500 text-sm">
          <div className="mb-2">✨ Nothing to clean up right now.</div>
          <div className="text-xs">Try running a fresh scan or check back after the nightly sweep.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(r => (
            <div key={r.rec_id} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-4" data-testid={`cleanup-rec-${r.rec_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${SEVERITY_COLORS[r.severity] || SEVERITY_COLORS.info}`}>{(r.severity || '').toUpperCase()}</span>
                    <span className="text-xs text-slate-500 uppercase">{KIND_LABEL[r.kind] || r.kind}</span>
                    <span className="text-xs text-slate-400">·</span>
                    <span className="text-xs text-slate-500 capitalize">{(r.object_type || '').replace(/_/g, ' ')}</span>
                  </div>
                  <div className="mt-1 font-semibold text-slate-800 dark:text-slate-200 truncate">{r.object_name || r.object_id}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">{r.reason}</div>
                  {r.ai_explanation && (
                    <div className="mt-2 pl-3 border-l-2 border-emerald-300 text-sm text-slate-700 dark:text-slate-300 italic">
                      <Sparkles size={12} className="inline mr-1 text-emerald-500" />{r.ai_explanation}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="default" onClick={() => onApprove(r.rec_id)} data-testid={`approve-${r.rec_id}`}>
                    Archive
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDismiss(r.rec_id)} data-testid={`dismiss-${r.rec_id}`}>
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
